import React, { useState } from 'react';
import {
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Box,
    Typography,
    Divider,
    ListItemIcon,
    Paper,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { styled } from '@mui/material/styles';
import { usePermissions } from '../../provider/PermissionProvider';
import logger from "../../utils/logger";
import { useNavigate } from 'react-router-dom';

const StyledMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 8,
        marginTop: 12,
        minWidth: 220,
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
        border: '1px solid',
        borderColor: theme.palette.divider,
    },
    '& .MuiMenu-list': {
        padding: '8px',
    },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    borderRadius: 6,
    padding: '10px 12px',
    margin: '2px 0',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },

    '& .MuiListItemIcon-root': {
        minWidth: 36,
        color: theme.palette.text.secondary,
    },
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
    padding: '8px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: 4,
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: 40,
    height: 40,
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`,
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
    transition: 'transform 0.2s ease-in-out',

    '&:hover': {
        transform: 'scale(1.05)',
    },

    '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
        color: '#ffffff',
    },
}));

const ProfileMenu = ({ onSignOut }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const { userProfile = {} } = usePermissions();
    const { fullName = '', email = '' } = userProfile;
    const navigate = useNavigate();

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleSignOut = () => {
        handleClose();
        onSignOut();
    };

    const handleProfile = () => {
        handleClose();
        // Add profile navigation logic here
        logger.log('Navigate to profile');
    };

    const handleSettings = () => {
        handleClose();
        // Add settings navigation logic here
        logger.log('Navigate to settings');
    };

    return (
        <>
            <IconButton
                onClick={handleOpen}
                sx={{
                    p: 0.5,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.05)',
                    },
                }}
            >
                <StyledAvatar>
                    <AccountCircleIcon />
                </StyledAvatar>
            </IconButton>

            <StyledMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {/* Profile Header */}
                <ProfileHeader
                    onClick={()=>{
                        handleClose(); 
                        navigate('/my/profile')
                    }}
                    sx={{ cursor: 'pointer' }}
                >
                    <StyledAvatar>
                        <AccountCircleIcon />
                    </StyledAvatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                color: 'text.primary',
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {fullName}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                            }}
                        >
                            {email}
                        </Typography>
                    </Box>
                </ProfileHeader>

                {/* Menu Items */}
                <StyledMenuItem sx={{ display: 'none' }} onClick={handleProfile}>
                    <ListItemIcon>
                        <PersonOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">My Profile</Typography>
                </StyledMenuItem>

                <Divider sx={{ my: 1, display: 'none' }} />

                <StyledMenuItem
                    onClick={handleSignOut}
                    sx={{
                        color: 'error.main',
                        '&:hover': {
                            backgroundColor: 'error.lighter',
                        },
                        '& .MuiListItemIcon-root': {
                            color: 'error.main',
                        },
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit" sx={{ fontWeight: 500 }}>
                        Sign Out
                    </Typography>
                </StyledMenuItem>
            </StyledMenu>
        </>
    );
};

export default ProfileMenu;